import { Component, model, input, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { InputNumber } from 'primeng/inputnumber';
import { LineItemModel } from '../../../../models/template.model';
import { SpeechService } from '../../../../services/speech.service';
import { ValidationService } from '../../../../services/validation.service';
import { parseValidationConfig, NumericValidationConfig } from '../../../../models/validation-config.model';

@Component({
  selector: 'app-integer-control',
  standalone: true,
  imports: [CommonModule, FormsModule, InputNumber],
  template: `
    <div class="checklist-control-row">
      <label [for]="'integer-' + lineItem().lineItemId" class="checklist-control-label">
        {{ lineItem().name }}@if (lineItem().isRequired) { <span class="required-indicator">*</span>}
      </label>
      <div class="checklist-control-input">
        <p-inputnumber
          [inputId]="'integer-' + lineItem().lineItemId"
          [(ngModel)]="value"
          [disabled]="disabled()"
          [useGrouping]="false"
          [showButtons]="true"
          [min]="configMin()"
          [max]="configMax()"
          placeholder="Enter a whole number"
          styleClass="w-full"
          [ngClass]="{'ng-invalid ng-dirty': hasError()}"
          (onBlur)="onBlur()"
        />
        @if (hasError()) {
          @for (err of errors(); track $index) {
            <small class="p-error d-block">{{ err }}</small>
          }
        } @else if (hint()) {
          <small class="text-color-secondary">{{ hint() }}</small>
        }
      </div>
      <div class="ctrl-speaker-col">
        <button type="button" class="ctrl-speaker-btn" (click)="speakLabel()"
          [attr.aria-label]="'Read label: ' + lineItem().name">
          <i class="ri-volume-up-line"></i>
        </button>
      </div>
    </div>
  `,
  styles: []
})
export class IntegerControlComponent {
  lineItem = input.required<LineItemModel>();
  value = model<number | null>(null);
  disabled = input<boolean>(false);
  showError = input<boolean>(false);

  private speechService = inject(SpeechService);
  private validationService = inject(ValidationService);

  touched = signal(false);

  private numericConfig = computed(() =>
    parseValidationConfig<NumericValidationConfig>(this.lineItem().validationConfig)
  );

  configMin = computed(() => this.numericConfig()?.min ?? 0);
  configMax = computed(() => this.numericConfig()?.max ?? undefined);

  errors = computed(() => {
    if (!this.showError() && !this.touched()) return [];
    return this.validationService.validate(this.lineItem(), this.value());
  });

  hasError = computed(() => this.errors().length > 0);

  hint = computed(() => this.hasError() ? '' : this.validationService.getHint(this.lineItem()));

  onBlur(): void {
    this.touched.set(true);
  }

  speakLabel(): void {
    this.speechService.speak(this.lineItem().name);
  }
}
